import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import {
  type BulkResponse,
  type BulkRow,
  type RoleAssignmentRequest,
  admin,
  bulkUsers,
} from "../../data/api";
import { useAction } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Failure,
  Hint,
  Mono,
  PageHeader,
  Section,
  Tag,
  Tags,
} from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import type { TenantPageProps } from "../pages";

// ---
// Bulk provisioning
//
// A dry run is mandatory before applying, because the failure modes are per-row and only a dry run
// makes them visible while they are still free to fix.
//
// Generated passwords are shown once, in the apply response, and never again. There is no mail
// transport yet, so they have to be distributed out of band — the UI says so rather than leaving
// someone to discover it.
// ---

const TEMPLATE = `email,username,assignments
op.one@example.com,op_one,netherlands:shell.store_operator|netherlands:iam.member|belgium:shell.reviewer
reviewer.one@example.com,rev_one,shell.reviewer`;

/**
 * Reads one `project:role` pair, or a bare role for the whole tenant.
 *
 * Only the first colon separates, so a role key keeps whatever it contains. Nothing here decides
 * whether the project or the role exists: the server knows the catalogue and says which pair was
 * wrong, and a second opinion in the browser could only disagree with it.
 */
function parseAssignment(text: string): RoleAssignmentRequest | null {
  const at = text.indexOf(":");
  const roleKey = (at === -1 ? text : text.slice(at + 1)).trim();
  const project = at === -1 ? "" : text.slice(0, at).trim();
  if (roleKey === "") return null;
  return { roleKey, ...(project === "" ? {} : { project }) };
}

/**
 * Parses the pasted CSV. Header optional; columns are email, username, assignments.
 *
 * The assignments column holds pairs rather than the file holding one project column, because a
 * person may need the same role in two projects — which a column could only express by naming
 * them on two lines, and two lines for one person is refused as a duplicate.
 */
function parseCsv(text: string): { rows: BulkRow[]; problems: string[] } {
  const rows: BulkRow[] = [];
  const problems: string[] = [];
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "" && !line.startsWith("#"));

  for (const [index, line] of lines.entries()) {
    const cells = line.split(",").map((cell) => cell.trim());
    if (index === 0 && cells[0]?.toLowerCase() === "email") continue;
    const [email, username, assignments] = cells;
    if (email === undefined || username === undefined || assignments === undefined) {
      problems.push(`Line ${index + 1}: expected email, username, assignments`);
      continue;
    }
    // Pipe-separated, because a comma is the column separator.
    const parsed = assignments
      .split("|")
      .map((each) => each.trim())
      .filter((each) => each !== "")
      .map(parseAssignment);
    if (parsed.some((each) => each === null)) {
      problems.push(`Line ${index + 1}: a pair names a project with no role after the colon`);
      continue;
    }
    rows.push({ email, username, assignments: parsed.filter((each) => each !== null) });
  }
  return { rows, problems };
}

// `scopes` and `projects` arrive from the wrapper but this page checks neither: the read gate is
// applied for it, and a bulk job names its own projects.
export function Bulk({ tenant, token }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const [text, setText] = useState(TEMPLATE);
  const [response, setResponse] = useState<BulkResponse | null>(null);
  const [assignable, setAssignable] = useState<string[] | null>(null);
  const action = useAction();

  const { rows, problems } = parseCsv(text);
  const dryRunClean = response?.dryRun === true && response.summary.errors === 0 && rows.length > 0;

  function run(dryRun: boolean): void {
    action.run(async () => {
      setResponse(await bulkUsers(tenant, token, rows, dryRun));
    });
  }

  return (
    <>
      <PageHeader
        title="Bulk add users"
        action={
          <Button
            variant="outlined"
            disabled={assignable !== null}
            onClick={() => {
              void admin
                .roles(tenant, token)
                .then((result) => setAssignable([...result.delegable]))
                .catch(() => setAssignable([]));
            }}
          >
            Show assignable roles
          </Button>
        }
      />

      {assignable !== null && (
        <Section title="Roles you may assign">
          {assignable.length === 0 ? (
            <Alert severity="warning">You may not assign any role.</Alert>
          ) : (
            <Tags>
              {assignable.map((key) => (
                <Tag key={key} label={key} />
              ))}
            </Tags>
          )}
        </Section>
      )}

      <Section title="Users">
        <TextField
          multiline
          minRows={8}
          fullWidth
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setResponse(null);
          }}
          spellCheck={false}
          InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: 12.5, lineHeight: 1.7 } }}
        />
        <Hint>
          Columns are <Mono>email,username,assignments</Mono>. Each assignment is{" "}
          <Mono>project:role</Mono>, separated by a pipe; a role on its own applies to the whole
          tenant. The same role may appear twice in different projects. One line per person — a
          second line for the same email is refused.
        </Hint>
        {problems.length > 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {problems.map((problem) => (
              <div key={problem}>{problem}</div>
            ))}
          </Alert>
        )}
        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            disabled={action.busy || rows.length === 0}
            onClick={() => run(true)}
          >
            Dry run · {rows.length} {rows.length === 1 ? "row" : "rows"}
          </Button>
          <Button
            variant="contained"
            disabled={action.busy || !dryRunClean}
            onClick={() => run(false)}
            title={dryRunClean ? "" : "A clean dry run is required first"}
          >
            Apply
          </Button>
        </Stack>
        <Failure error={action.error} />
      </Section>

      {response !== null && (
        <Section
          flush
          title={response.dryRun ? "Dry run" : "Applied"}
          action={
            <Tags>
              <StatusBadge label={`${response.summary.created} created`} tone="success" />
              <StatusBadge label={`${response.summary.existing} existing`} tone="neutral" />
              <StatusBadge
                label={`${response.summary.errors} errors`}
                tone={response.summary.errors > 0 ? "danger" : "neutral"}
              />
            </Tags>
          }
        >
          {response.note !== undefined && (
            <Box sx={{ px: 5, pt: 3 }}>
              <Alert severity="warning">{response.note}</Alert>
            </Box>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {response.results.map((result) => (
                <TableRow key={result.username} hover>
                  <TableCell>
                    <StatusBadge
                      label={result.status}
                      tone={
                        result.status === "error"
                          ? "danger"
                          : result.status === "created"
                            ? "success"
                            : "neutral"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Mono>{result.username}</Mono>
                  </TableCell>
                  <TableCell>{result.email}</TableCell>
                  <TableCell>
                    {result.inviteUrl !== undefined ? (
                      <Mono sx={{ fontSize: 11, wordBreak: "break-all" }}>{result.inviteUrl}</Mono>
                    ) : (
                      <Box component="span" sx={{ color: tokens.ink.muted }}>
                        {result.detail}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}
    </>
  );
}
